import { Injectable, NgZone, inject, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatSoundService } from './chat-sound.service';
import { chatHubUrl } from './chat-api.service';
import { getBusinessKey } from '../config/tenant.config';
import {
  ChatMessage,
  ConversationListItem,
  UnreadCount
} from '../models/chat.model';

/** Quiet fallback interval while SignalR is fully closed (not during reconnect attempts). */
const FALLBACK_RESTART_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService {
  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);
  private readonly sound = inject(ChatSoundService);
  private connection: signalR.HubConnection | null = null;
  /** Business key the current admin hub was opened with. */
  private adminBusinessKey: string | null = null;
  private adminIntentionalStop = false;
  private adminRestartTimer: ReturnType<typeof setTimeout> | null = null;
  /** Serializes admin start/stop so concurrent callers cannot abort negotiation. */
  private adminGate: Promise<void> = Promise.resolve();

  private visitorConnection: signalR.HubConnection | null = null;
  private visitorConversationId: string | null = null;
  private visitorKey: string | null = null;
  private visitorIntentionalStop = false;
  private visitorRestartTimer: ReturnType<typeof setTimeout> | null = null;
  private visitorGate: Promise<void> = Promise.resolve();

  private readonly conversationUpsertSubject = new Subject<ConversationListItem>();
  private readonly messageSubject = new Subject<ChatMessage>();
  private readonly adminSyncSubject = new Subject<void>();
  private readonly visitorSyncSubject = new Subject<void>();
  private readonly adminFallbackSubject = new BehaviorSubject<boolean>(false);
  private readonly visitorFallbackSubject = new BehaviorSubject<boolean>(false);

  readonly conversationUpserted$ = this.conversationUpsertSubject.asObservable();
  readonly messageReceived$ = this.messageSubject.asObservable();
  /** Fired once after admin SignalR reconnect / recovery — do a single REST sync. */
  readonly adminSyncNeeded$ = this.adminSyncSubject.asObservable();
  /** Fired once after visitor SignalR reconnect / recovery — do a single REST sync. */
  readonly visitorSyncNeeded$ = this.visitorSyncSubject.asObservable();
  /** True only when hub is closed and automatic reconnect has given up. */
  readonly adminFallback$ = this.adminFallbackSubject.asObservable();
  readonly visitorFallback$ = this.visitorFallbackSubject.asObservable();

  readonly unreadCount = signal(0);
  readonly mute = this.sound.mute;
  readonly connected = signal(false);
  readonly visitorConnected = signal(false);

  async startAdmin(): Promise<void> {
    return this.runExclusive(this.adminGate, (next) => {
      this.adminGate = next;
    }, () => this.startAdminLocked());
  }

  async stopAdmin(): Promise<void> {
    return this.runExclusive(this.adminGate, (next) => {
      this.adminGate = next;
    }, () => this.stopAdminConnection(true));
  }

  async startVisitor(conversationId: string, visitorKey: string): Promise<void> {
    return this.runExclusive(this.visitorGate, (next) => {
      this.visitorGate = next;
    }, () => this.startVisitorLocked(conversationId, visitorKey));
  }

  async stopVisitor(clearIds = true): Promise<void> {
    return this.runExclusive(this.visitorGate, (next) => {
      this.visitorGate = next;
    }, () => this.stopVisitorConnection(clearIds));
  }

  private async startAdminLocked(): Promise<void> {
    this.sound.armUnlock();
    const role = this.auth.user()?.role;
    if (role !== 'BusinessAdmin') {
      return;
    }

    const token = this.auth.validToken();
    if (!token) {
      return;
    }

    const businessKey = this.auth.businessKey() ?? '';
    const sameBusiness =
      this.adminBusinessKey === businessKey &&
      this.connection?.state === signalR.HubConnectionState.Connected;
    if (sameBusiness) {
      this.connected.set(true);
      this.adminFallbackSubject.next(false);
      return;
    }

    await this.stopAdminConnection(true);
    this.adminIntentionalStop = false;
    this.adminBusinessKey = businessKey;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${chatHubUrl()}?businessKey=${encodeURIComponent(businessKey)}`, {
        accessTokenFactory: () => this.auth.validToken() ?? '',
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    this.connection = connection;

    connection.on('conversationUpserted', (item: ConversationListItem) => {
      this.zone.run(() => this.conversationUpsertSubject.next(this.normalizeConversation(item)));
    });
    connection.on('conversationEngaged', (item: ConversationListItem) => {
      this.zone.run(() => {
        this.conversationUpsertSubject.next(this.normalizeConversation(item));
        this.sound.play();
      });
    });
    connection.on('messageReceived', (message: ChatMessage) => {
      this.zone.run(() => {
        const normalized = this.normalizeMessage(message);
        this.messageSubject.next(normalized);
        if (normalized.senderType === 'Visitor') {
          this.sound.play();
        }
      });
    });
    connection.on('unreadCountChanged', (dto: UnreadCount | { Count?: number }) => {
      this.zone.run(() => {
        const count = (dto as UnreadCount).count ?? (dto as { Count?: number }).Count ?? 0;
        this.unreadCount.set(count);
      });
    });

    connection.onreconnecting(() => {
      this.zone.run(() => {
        this.connected.set(false);
        // Still retrying — do not start fallback polling yet.
        this.adminFallbackSubject.next(false);
      });
    });
    connection.onreconnected(() => {
      this.zone.run(() => {
        this.connected.set(true);
        this.adminFallbackSubject.next(false);
        this.clearAdminRestartTimer();
        this.adminSyncSubject.next();
      });
    });
    connection.onclose(() => {
      this.zone.run(() => {
        this.connected.set(false);
        if (this.adminIntentionalStop) {
          return;
        }
        this.adminFallbackSubject.next(true);
        this.scheduleAdminRestart();
      });
    });

    try {
      await connection.start();
      if (this.connection !== connection) {
        return;
      }
      this.zone.run(() => {
        this.connected.set(true);
        this.adminFallbackSubject.next(false);
      });
    } catch (err) {
      if (this.connection !== connection || this.isNegotiationAbort(err)) {
        return;
      }
      console.error('Chat hub (admin) failed to start', err);
      this.zone.run(() => {
        this.connected.set(false);
        this.adminFallbackSubject.next(true);
      });
      if (this.connection === connection) {
        this.connection = null;
        this.adminBusinessKey = null;
      }
      this.scheduleAdminRestart();
    }
  }

  private async startVisitorLocked(conversationId: string, visitorKey: string): Promise<void> {
    const alreadyConnected =
      this.visitorConnection?.state === signalR.HubConnectionState.Connected &&
      this.visitorConversationId === conversationId &&
      this.visitorKey === visitorKey;

    this.visitorConversationId = conversationId;
    this.visitorKey = visitorKey;

    if (alreadyConnected) {
      try {
        await this.visitorConnection!.invoke('JoinConversation', conversationId, visitorKey);
      } catch (err) {
        console.error('JoinConversation failed', err);
      }
      this.visitorConnected.set(true);
      this.visitorFallbackSubject.next(false);
      return;
    }

    await this.stopVisitorConnection(false);
    this.visitorIntentionalStop = false;
    this.visitorConversationId = conversationId;
    this.visitorKey = visitorKey;

    const businessKey = getBusinessKey();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${chatHubUrl()}?businessKey=${encodeURIComponent(businessKey)}`, {
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    this.visitorConnection = connection;

    connection.on('messageReceived', (message: ChatMessage) => {
      this.zone.run(() => this.messageSubject.next(this.normalizeMessage(message)));
    });

    connection.onreconnecting(() => {
      this.zone.run(() => {
        this.visitorConnected.set(false);
        this.visitorFallbackSubject.next(false);
      });
    });

    connection.onreconnected(async () => {
      await this.rejoinVisitorConversation();
      this.zone.run(() => {
        this.visitorConnected.set(true);
        this.visitorFallbackSubject.next(false);
        this.clearVisitorRestartTimer();
        this.visitorSyncSubject.next();
      });
    });

    connection.onclose(() => {
      this.zone.run(() => {
        this.visitorConnected.set(false);
        if (this.visitorIntentionalStop) {
          return;
        }
        this.visitorFallbackSubject.next(true);
        this.scheduleVisitorRestart();
      });
    });

    try {
      await connection.start();
      if (this.visitorConnection !== connection) {
        return;
      }
      await connection.invoke('JoinConversation', conversationId, visitorKey);
      if (this.visitorConnection !== connection) {
        return;
      }
      this.zone.run(() => {
        this.visitorConnected.set(true);
        this.visitorFallbackSubject.next(false);
      });
    } catch (err) {
      if (this.visitorConnection !== connection || this.isNegotiationAbort(err)) {
        return;
      }
      console.error('Chat hub (visitor) failed to start', err);
      this.zone.run(() => {
        this.visitorConnected.set(false);
        this.visitorFallbackSubject.next(true);
      });
      if (this.visitorConnection === connection) {
        this.visitorConnection = null;
      }
      this.scheduleVisitorRestart();
    }
  }

  setMute(muted: boolean): void {
    this.sound.setMute(muted);
  }

  setUnread(count: number): void {
    this.unreadCount.set(count);
  }

  /** Play beep for a newly observed visitor message (e.g. from fallback poll). */
  notifyVisitorMessage(): void {
    this.sound.play();
  }

  private runExclusive(
    gate: Promise<void>,
    setGate: (next: Promise<void>) => void,
    fn: () => Promise<void>
  ): Promise<void> {
    const run = gate.then(fn, fn);
    setGate(
      run.then(
        () => undefined,
        () => undefined
      )
    );
    return run;
  }

  private isNegotiationAbort(err: unknown): boolean {
    if (!err || typeof err !== 'object') {
      return false;
    }
    const e = err as { name?: string; message?: string };
    return (
      e.name === 'AbortError' ||
      (typeof e.message === 'string' && e.message.includes('stopped during negotiation'))
    );
  }

  private async stopAdminConnection(clearKey: boolean): Promise<void> {
    this.adminIntentionalStop = true;
    this.clearAdminRestartTimer();
    this.adminFallbackSubject.next(false);
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {
        /* ignore */
      }
      this.connection = null;
    }
    if (clearKey) {
      this.adminBusinessKey = null;
    }
    this.connected.set(false);
  }

  private async stopVisitorConnection(clearIds: boolean): Promise<void> {
    this.visitorIntentionalStop = true;
    this.clearVisitorRestartTimer();
    this.visitorFallbackSubject.next(false);
    if (this.visitorConnection) {
      try {
        await this.visitorConnection.stop();
      } catch {
        /* ignore */
      }
      this.visitorConnection = null;
    }
    if (clearIds) {
      this.visitorConversationId = null;
      this.visitorKey = null;
    }
    this.visitorConnected.set(false);
  }

  private async rejoinVisitorConversation(): Promise<void> {
    if (!this.visitorConversationId || !this.visitorKey || !this.visitorConnection) {
      return;
    }
    try {
      await this.visitorConnection.invoke(
        'JoinConversation',
        this.visitorConversationId,
        this.visitorKey
      );
    } catch (err) {
      console.error('Re-JoinConversation failed', err);
    }
  }

  private scheduleAdminRestart(): void {
    if (this.adminRestartTimer || this.adminIntentionalStop) {
      return;
    }
    this.adminRestartTimer = setTimeout(() => {
      this.adminRestartTimer = null;
      if (this.adminIntentionalStop || !this.adminFallbackSubject.value) {
        return;
      }
      void this.startAdmin().then(() => {
        if (this.connected()) {
          this.adminSyncSubject.next();
        }
      });
    }, FALLBACK_RESTART_MS);
  }

  private scheduleVisitorRestart(): void {
    if (this.visitorRestartTimer || this.visitorIntentionalStop) {
      return;
    }
    if (!this.visitorConversationId || !this.visitorKey) {
      return;
    }
    this.visitorRestartTimer = setTimeout(() => {
      this.visitorRestartTimer = null;
      if (this.visitorIntentionalStop || !this.visitorFallbackSubject.value) {
        return;
      }
      const id = this.visitorConversationId;
      const key = this.visitorKey;
      if (!id || !key) {
        return;
      }
      void this.startVisitor(id, key).then(() => {
        if (this.visitorConnected()) {
          this.visitorSyncSubject.next();
        }
      });
    }, FALLBACK_RESTART_MS);
  }

  private clearAdminRestartTimer(): void {
    if (this.adminRestartTimer) {
      clearTimeout(this.adminRestartTimer);
      this.adminRestartTimer = null;
    }
  }

  private clearVisitorRestartTimer(): void {
    if (this.visitorRestartTimer) {
      clearTimeout(this.visitorRestartTimer);
      this.visitorRestartTimer = null;
    }
  }

  private normalizeMessage(message: ChatMessage): ChatMessage {
    const raw = message as ChatMessage & {
      ConversationId?: string;
      SenderType?: string;
      SenderUserId?: string | null;
      CreatedAt?: string;
      IsReadByAdmin?: boolean;
      Id?: string;
      Body?: string;
    };
    return {
      id: raw.id ?? raw.Id ?? '',
      conversationId: raw.conversationId ?? raw.ConversationId ?? '',
      body: raw.body ?? raw.Body ?? '',
      senderType: (raw.senderType ?? raw.SenderType ?? 'Visitor') as ChatMessage['senderType'],
      senderUserId: raw.senderUserId ?? raw.SenderUserId ?? null,
      createdAt: raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString(),
      isReadByAdmin: raw.isReadByAdmin ?? raw.IsReadByAdmin ?? false
    };
  }

  private normalizeConversation(item: ConversationListItem): ConversationListItem {
    const raw = item as ConversationListItem & {
      Id?: string;
      VisitorKey?: string;
      Status?: string;
      LastMessageAt?: string;
      CreatedAt?: string;
      UnreadCount?: number;
      LastMessagePreview?: string | null;
    };
    return {
      id: raw.id ?? raw.Id ?? '',
      visitorKey: raw.visitorKey ?? raw.VisitorKey ?? '',
      status: raw.status ?? raw.Status ?? 'Open',
      lastMessageAt: raw.lastMessageAt ?? raw.LastMessageAt ?? new Date().toISOString(),
      createdAt: raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString(),
      unreadCount: raw.unreadCount ?? raw.UnreadCount ?? 0,
      lastMessagePreview: raw.lastMessagePreview ?? raw.LastMessagePreview ?? null
    };
  }
}
